import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {catchError, Observable, throwError} from "rxjs";
import {ErrorBase} from "../../model/ErrorBase";
import {ToastNotification, ToastNotificationsService} from "./toast-notification.service";
import * as Sentry from "@sentry/angular";
import {ErrorModalDetails, ErrorModalService} from "./error-modal.service";

@Injectable({
    providedIn: 'root'
    })
export class HttpErrorsInterceptorService implements HttpInterceptor {

    constructor(
    private toasts: ToastNotificationsService,
    private errorModalService: ErrorModalService,
  ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpEvent<any>> {
        return next.handle(req).pipe(catchError(err => {
          let error = this.toErrorBase(err, req);
            if (err.status === 0 ) {
              error = new ErrorBase("Communication Error");
              error.userMessage = `Connection error for ${req.method} ${req.url}. Check your Internet connection or try again later.`;
              error.errorCode = 'CONNECTION_ERROR';
              error.interactive = false;
              error.developerMessage = 'Request failed. Check the connection or server status.';
            } else if (error && error.interactive) {
              // skip regular error processing
              return throwError(() => error);
            } else if( err.status === 404 && err.error.developerMessage === "provided code is missing"){
              this.errorModalService.display(new ErrorModalDetails('ERROR_MODAL.TITLE', "ERROR_MODAL.MESSAGE_LOG_OUT", 8, true, false))
              return throwError(() => ({status: err.status, developerMessage: err.error.developerMessage}));
            } else if (err.status === 412 || err.status === 498 || err.status === 408 || err.status === 423) {
              return throwError(() => ({status: err.status}));
            } else if(err.status == 503) {
              error = new ErrorBase('No internet connection');
              error.errorCode = `HTTP:${err.status}`;
              error.userMessage = "Check your Internet connection or try again later.";
              error.developerMessage = err.error;
            } else if (err.status == 404) {
              return throwError(() => err);
            }
            let title = error.message;
            if (title === "Internal server error.") title = error.developerMessage;
            if (err.error?.error === "Internal Server Error") title = err.error.error
            let message = error.userMessage
            if (!message) message = ''
            const eventId = Sentry.captureException(error)
            this.toasts.display(new ToastNotification(`${title}`,`${message}`, true, true, null, null, null, eventId))

            return throwError(() => error);
          }));
    }

  private toErrorBase(err: any, req: HttpRequest<any>): ErrorBase {
    const payload = err?.error;
    const maybeErrorBase = payload as ErrorBase | null;
    if (maybeErrorBase?.message || maybeErrorBase?.developerMessage || maybeErrorBase?.userMessage) {
      maybeErrorBase.status = err?.status;
      maybeErrorBase.errorCode = this.resolveDomainErrorCode(payload) ?? maybeErrorBase.errorCode;
      return maybeErrorBase;
    }

    const hasProblemDetail = payload && (payload.title || payload.detail || payload.type || payload.status);
    if (hasProblemDetail) {
      const error = new ErrorBase(payload.title || `HTTP ${err?.status ?? 'ERROR'}`);
      error.userMessage = payload.detail || payload.title || '';
      error.developerMessage = payload.detail || payload.title || '';
      error.errorCode = this.resolveDomainErrorCode(payload) ?? `${payload.status ?? err?.status ?? 'UNKNOWN'}`;
      error.status = err?.status;
      error.interactive = false;
      return error;
    }

    const error = new ErrorBase("Communication Error");
    error.userMessage = 'Server Error';
    error.developerMessage = payload ?? err?.message ?? `Request failed for ${req.method} ${req.url}`;
    error.errorCode = this.resolveDomainErrorCode(payload) ?? `HTTP:${err?.status ?? 'UNKNOWN'}`;
    error.status = err?.status;
    error.interactive = false;
    return error;
  }

  private resolveDomainErrorCode(payload: any): string | null {
    if (!payload) {
      return null;
    }
    if (typeof payload === 'object' && payload.errorCode) {
      return String(payload.errorCode);
    }
    return null;
  }

}
